import type { SUPPORTED_FORMATTER_TYPE } from "code-formatting/constants";
import type { WidgetProps } from "legacy/widgets";
import type { ApiUnion } from "store/slices/apisShared";
import type { EventDefinitionScoped } from "store/slices/application/events/eventConstants";
import type { AppStateVarScoped } from "store/slices/application/stateVars/StateConstants";
import type { AppTimerScoped } from "store/slices/application/timers/TimerConstants";

import type { ApiInfo } from "./ApiConstants";

type EditorTabWithName = EditorOpenTab & {
  name: string;
};

export type EditorOpenTabWithAPI = EditorTabWithName & {
  entityType: EditorOpenTabType.API;
  entity: ApiUnion;
};

export type EditorOpenTabWithAPIInfo = EditorTabWithName & {
  entityType: EditorOpenTabType.API_INFO;
  entity: ApiInfo;
};

export type EditorOpenTabWithWidget = EditorTabWithName & {
  entityType: EditorOpenTabType.WIDGET;
  entity: WidgetProps;
};

export type EditorOpenTabWithTimer = EditorTabWithName & {
  entityType: EditorOpenTabType.TIMER;
  entity: AppTimerScoped;
};

export type EditorOpenTabWithStateVar = EditorTabWithName & {
  entityType: EditorOpenTabType.STATE_VAR;
  entity: AppStateVarScoped;
};

export type EditorOpenTabWithEvent = EditorTabWithName & {
  entityType: EditorOpenTabType.CUSTOM_EVENT;
  entity: EventDefinitionScoped;
};

export type EditorOpenTabWithEntity =
  | EditorOpenTabWithAPI
  | EditorOpenTabWithAPIInfo
  | EditorOpenTabWithWidget
  | EditorOpenTabWithTimer
  | EditorOpenTabWithStateVar
  | EditorOpenTabWithEvent;

export enum EditorOpenTabType {
  API = "API",
  API_INFO = "API_INFO",
  WIDGET = "WIDGET",
  TIMER = "TIMER",
  STATE_VAR = "STATE_VAR",
  CUSTOM_EVENT = "CUSTOM_EVENT",
}

export type EditorOpenTab = {
  id?: string;
  entityType: EditorOpenTabType;
  entityId: string;
  widgetProperty?: string;
};

type PageId = string;
export type EditorAppPreferences = Record<PageId, PagePreferences>;
export type PagePreferences = {
  openBottomPanelTabs: EditorOpenTab[];
};

enum DeveloperPreferencesKeys {
  Shared = "shared",
  Application = "application",
}

// Developer preferences that can be customized per EntityType
export type ApplicationDeveloperPreferences = {
  triggerFrontendEventHandlersOnManualRun: boolean;
};

export enum KeyBindings {
  Default = "default",
  Vim = "vim",
}

export enum AiContextMode {
  AllKeys = "all_keys",
  SqlStepKeys = "sql_step_keys",
  OnlySuperblocksKeys = "only_superblocks_keys",
}

// Developer preferences shared between EntityTypes
export type SharedDeveloperPreferences = {
  formatCodeOnBlur: boolean;
  formatCodeOnRun: boolean;
  languagesToFormat: SUPPORTED_FORMATTER_TYPE[];
  languagesToAiCompletion: SUPPORTED_FORMATTER_TYPE[];
  aiContextMode: AiContextMode;
  keyBindings: KeyBindings;
};

export type DeveloperPreferences = {
  [DeveloperPreferencesKeys.Shared]: SharedDeveloperPreferences;
  [DeveloperPreferencesKeys.Application]: ApplicationDeveloperPreferences;
};
